import PropTypes from 'prop-types';
import { useContext } from 'react';
import EverTrue from 'app';
import { useFeatureFlag } from 'entities/helpers/use-feature-flag';
import { TasksNotificationContext } from 'apps/tasks/hooks/tasks-notification-context';
import DotNotificationBadge from 'components/notifications/dot-notification-badge.js';
import NavigationExpandableItem from 'apps/layout/components/navigation-expandable-item';
import NavigationNestedItem from 'apps/layout/components/navigation-nested-item';
import NavigationSubItem from 'apps/layout/components/navigation-sub-item';
import NewBadge from 'components/elements/new-badge';

/**
 * Outreach Planner navigation link with feature-flag-driven rendering:
 * - Signal only: simple "Cadences" link to /cadences
 * - Cadence V2: expandable section with Cadences 1.0 and Cadences 2.0
 * - Neither flag: hidden
 */
const NavigationOutreachPlannerLink = ({ active_path = '', isCollapsed }) => {
  const { flagEnabled: cadenceV2Enabled, loaded: cadenceV2Loaded } = useFeatureFlag('cadenceV2Internal');
  const { flagEnabled: signalEnabled, loaded: signalLoaded } = useFeatureFlag('signal');
  const { tasksDueCount = {} } = useContext(TasksNotificationContext);
  const journeysDueCount = tasksDueCount?.journeys || 0;

  if (!cadenceV2Loaded || !signalLoaded) return null;
  if (!signalEnabled) return null;

  const cadencesRegex = /^\/cadences.*/;
  const cadences2Regex = /^\/cadences-v2.*/;

  // Signal only (no cadence v2) — render a simple Cadences link
  if (signalEnabled && !cadenceV2Enabled) {
    const isActive = !!active_path.match(cadencesRegex);
    return (
      <NavigationSubItem
        icon="cadences"
        iconKit="gt2"
        label="Cadences"
        isActive={isActive}
        onClick={() => {
          EverTrue.Navigator('/cadences', true);
        }}
      >
        <DotNotificationBadge
          popoverText={'Incomplete Cadence Steps Due Today'}
          showBadge={!!journeysDueCount}
          style={{ marginLeft: isCollapsed ? '5px' : '8px' }}
        />
      </NavigationSubItem>
    );
  }

  // Cadence V2 enabled — render expandable nav with sub-items
  const isParentActive = !!active_path.match(cadencesRegex) || !!active_path.match(cadences2Regex);
  const isCadences1Active = !!active_path.match(cadencesRegex) && !active_path.match(cadences2Regex);
  const isCadences2Active = !!active_path.match(cadences2Regex);

  return (
    <NavigationExpandableItem
      icon="cadences"
      iconKit="gt2"
      label="Outreach Planner"
      isActive={isParentActive}
      defaultExpanded={isParentActive}
      badge={
        <DotNotificationBadge
          popoverText={'Incomplete Cadence Steps Due Today'}
          showBadge={!!journeysDueCount}
          style={{ marginLeft: isCollapsed ? '5px' : '8px' }}
        />
      }
    >
      <NavigationNestedItem
        label={cadenceV2Enabled ? 'Cadences 1.0' : 'Cadences'}
        path="/cadences"
        isActive={isCadences1Active}
        trackingType="journeys_v1"
      />
      <NavigationNestedItem
        label="Cadences 2.0"
        path="/cadences-v2"
        isActive={isCadences2Active}
        trackingType="journeys_v2"
        badge={<NewBadge />}
      />
    </NavigationExpandableItem>
  );
};

NavigationOutreachPlannerLink.propTypes = {
  active_path: PropTypes.string,
  isCollapsed: PropTypes.bool,
};

export default NavigationOutreachPlannerLink;
